import { ANALYTICS_READS, PULSE_READ, analyticsRead } from "./access.ts";
import {
  ANALYTICS_METRICS_PATH,
  METRIC_DIMENSIONS,
  METRIC_FILTERS,
  METRIC_FILTER_PARAMS,
  metricReadZones,
  metricZone,
} from "./analytics-metrics.ts";
import type { AnalyticsZone, PermissionKey } from "./access.ts";

export type AnalyticsGate = AnalyticsZone | "any" | "settings" | "pulse";

const growth = (paths: readonly string[]): readonly [string, AnalyticsGate][] =>
  paths.map((path) => [path, "growth"]);

const engagement = (paths: readonly string[]): readonly [string, AnalyticsGate][] =>
  paths.map((path) => [path, "engagement"]);

const audience = (paths: readonly string[]): readonly [string, AnalyticsGate][] =>
  paths.map((path) => [path, "audience"]);

const profiles = (paths: readonly string[]): readonly [string, AnalyticsGate][] =>
  paths.map((path) => [path, "profiles"]);

const shared = (paths: readonly string[]): readonly [string, AnalyticsGate][] =>
  paths.map((path) => [path, "any"]);

const ANALYTICS_ROUTE_GATES: ReadonlyMap<string, AnalyticsGate> = new Map([
  ...growth([
    "/api/analytics/growth/invite-codes",
    "/api/analytics/distributions/leaver-tenure",
    "/api/analytics/cohorts/retention",
    "/api/analytics/cohorts/retention-curve",
    "/api/analytics/cohorts/activation",
    "/api/analytics/cohorts/role-acquisition",
    "/api/analytics/inviters",
  ]),
  ...engagement([
    "/api/analytics/emojis",
    "/api/analytics/attendance",
    "/api/analytics/distributions/reply-latency",
    "/api/analytics/conversation-pairs",
    "/api/analytics/commands",
  ]),
  ...audience([
    "/api/analytics/apps",
    "/api/analytics/distributions/account-age",
    "/api/analytics/distributions/dormancy",
  ]),
  ...profiles(["/api/analytics/members/search"]),
  ...shared([
    ANALYTICS_METRICS_PATH,
    "/api/analytics/annotations",
    "/api/analytics/annotations/{annotationId}",
    "/api/analytics/annotations/impact",
    "/api/analytics/annotations/rules",
    "/api/analytics/annotations/rules/{ruleId}",
    "/api/analytics/annotations/rules/preview",
    "/api/analytics/entities/users",
    "/api/analytics/entities/channels",
    "/api/analytics/entities/roles",
    "/api/analytics/entities/emojis",
    "/api/analytics/entities/apps",
    "/api/analytics/entities/refresh",
    "/api/analytics/disclosure",
    "/api/analytics/coverage/bounds",
    "/api/analytics/findings",
  ]),
  ["/api/analytics/pulse", "pulse"],
]);

const MODULE_ROUTE_GATES: ReadonlyMap<string, AnalyticsGate> = new Map(
  profiles([
    "/api/leveling/members",
    "/api/leveling/members/{userId}",
    "/api/leveling/members/{userId}/adjustments",
    "/api/leveling/member/days",
    "/api/leveling/deliveries",
    "/api/leveling/pace",
    "/api/leveling/preview",
    "/api/leveling/preview/member",
  ]),
);

const MEMBER_NAMING_ROUTES: ReadonlySet<string> = new Set([
  "/api/analytics/inviters",
  "/api/analytics/growth/invite-codes",
  "/api/analytics/conversation-pairs",
]);

const TENANT_PREFIX = /^\/t\/[^/]+/;

const ROUTER_PARAMETER = /:(\w+)/g;

const routeOf = (path: string): string =>
  path.replace(TENANT_PREFIX, "").replace(ROUTER_PARAMETER, "{$1}");

export const analyticsGateOf = (path: string): AnalyticsGate | undefined =>
  ANALYTICS_ROUTE_GATES.get(routeOf(path));

export const namesMembers = (path: string): boolean => MEMBER_NAMING_ROUTES.has(routeOf(path));

export const routeGateOf = (path: string): AnalyticsGate | undefined => {
  const route = routeOf(path);
  return ANALYTICS_ROUTE_GATES.get(route) ?? MODULE_ROUTE_GATES.get(route);
};

export const analyticsGateSatisfied = (
  gate: AnalyticsGate,
  permissions: readonly string[],
): boolean => {
  switch (gate) {
    case "any":
      return ANALYTICS_READS.some((key) => permissions.includes(key));
    case "settings":
      return true;
    case "pulse":
      return permissions.includes(PULSE_READ);
    default:
      return permissions.includes(analyticsRead(gate));
  }
};

export const analyticsGateKeys = (gate: AnalyticsGate): readonly PermissionKey[] => {
  switch (gate) {
    case "any":
      return ANALYTICS_READS;
    case "settings":
      return [];
    case "pulse":
      return [PULSE_READ];
    default:
      return [analyticsRead(gate)];
  }
};

export interface ReadFace {
  readonly path: string;
  readonly metrics: readonly string[];
  readonly zones: readonly AnalyticsZone[];
}

const listedIn = (query: Readonly<Record<string, unknown>>, name: string): readonly string[] => {
  const asked = query[name];
  return Array.isArray(asked) ? asked.map(String) : [];
};

export const readFace = (path: string, query: Readonly<Record<string, unknown>>): ReadFace => {
  if (!path.endsWith(ANALYTICS_METRICS_PATH)) return { path, metrics: [], zones: [] };
  const metrics = listedIn(query, "metrics");
  const grouping = listedIn(query, "groupBy");
  const sent = Object.keys(query);
  return {
    path,
    metrics,
    zones: metricReadZones({
      metrics,
      groupBy: METRIC_DIMENSIONS.filter((dimension) => grouping.includes(dimension)),
      filters: METRIC_FILTERS.filter((filter) =>
        METRIC_FILTER_PARAMS[filter].some((param) => sent.includes(param)),
      ),
    }),
  };
};

export const analyticsReadAllowed = (face: ReadFace, permissions: readonly string[]): boolean => {
  const gate = routeGateOf(face.path);
  if (gate === undefined || gate === "settings") return false;
  return (
    analyticsGateSatisfied(gate, permissions) &&
    (!namesMembers(face.path) || analyticsGateSatisfied("profiles", permissions)) &&
    face.metrics.every((metric) => metricZone(metric) !== undefined) &&
    face.zones.every((zone) => analyticsGateSatisfied(zone, permissions))
  );
};
